import {has, pluck, uniq} from 'ramda';
import {isPlainObj} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {ApiError, isFetchBaseQueryError} from 'shared/web';

import {ApiException} from './types/ApiException';

export function isApiException(errorData: unknown): errorData is ApiException {
  return !!(isPlainObj(errorData) && has('errors', errorData));
}

const NO_CODE = '???';

type ParsedApiErrorDetail = {
  loc?: unknown;
  msg?: unknown;
  input?: unknown;
};

type ParsedApiErrorMessage = {
  detail?: unknown;
};

function isObject(value: unknown): value is Record<string, unknown> {
  return isPlainObj(value);
}

function parseJsonErrorMessage(message: string): ParsedApiErrorMessage | null {
  try {
    const parsedMessage: unknown = JSON.parse(message);

    return isObject(parsedMessage) ? parsedMessage : null;
  } catch {
    return null;
  }
}

function formatDetailLocation(loc: unknown): string | null {
  if (!Array.isArray(loc)) {
    return null;
  }

  const location = loc
    .filter((item): item is string | number => typeof item === 'string' || typeof item === 'number')
    .filter((item) => item !== 'body')
    .join('.');

  return location || null;
}

function formatDetailInput(input: unknown): string | null {
  if (typeof input === 'string' || typeof input === 'number' || typeof input === 'boolean') {
    return String(input);
  }

  return null;
}

function formatParsedApiErrorDetail(detail: ParsedApiErrorDetail): string | null {
  if (typeof detail.msg !== 'string') {
    return null;
  }

  const location = formatDetailLocation(detail.loc);
  const input = formatDetailInput(detail.input);

  return [location ? `${location}: ` : '', detail.msg, input ? ` (received ${input})` : ''].join(
    ''
  );
}

export function getNestedApiErrorMessages(message: unknown): string[] {
  if (typeof message !== 'string') {
    return [];
  }

  const parsedMessage = parseJsonErrorMessage(message);

  if (!Array.isArray(parsedMessage?.detail)) {
    return [];
  }

  return parsedMessage.detail
    .filter(isObject)
    .map(formatParsedApiErrorDetail)
    .filter((message): message is string => !!message);
}

/**
 * @about Parses catched error from RTK Query
 */
export function getApiErrorMessages(error: ApiError, defaultErrorMessage?: string) {
  const defaultMessage = defaultErrorMessage ?? i18n.t('general.notifications.defaultErrorMessage');
  if (!isFetchBaseQueryError(error) || !isApiException(error?.data)) {
    return defaultMessage;
  }

  /**
   * Some proxy endpoints return validation details as JSON inside errors[].message with code "???".
   */
  const nestedErrorMessages = uniq(
    error.data.errors.flatMap((error) => getNestedApiErrorMessages(error?.message))
  );

  if (nestedErrorMessages.length) {
    return nestedErrorMessages;
  }

  /**
   * Filter out messages without proper code
   * TODO: Translate messages by code
   */
  const errors = error?.data?.errors.filter(
    (error) => error?.message && error?.code && error?.code !== NO_CODE
  );

  /**
   * Get unique error messsages
   */
  const messages = uniq(pluck('message', errors));

  return messages.length ? messages : defaultMessage;
}
